import { useState } from 'react';
import { ArrowRight, Eye, EyeOff, Lock, ShieldCheck, User } from 'lucide-react';
import {
  clearBusinessContext,
  getAccountantSeaweedAccessTabs,
  setPortalSessionContext,
} from '../app/businessContext';
import { getAccountantEntryPath } from '../app/routes';
import {
  fetchEPortalBusinesses,
  findSeaweedBusiness,
  loginToEPortal,
} from '../services/portalLoginService';

function LoginPage() {
  const [formValues, setFormValues] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  function handleChange(event) {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      clearBusinessContext();

      const loginResponse = await loginToEPortal(formValues);
      const businessesResponse = await fetchEPortalBusinesses(loginResponse.token);
      const business = findSeaweedBusiness(businessesResponse.data || [], loginResponse.user);

      if (!business) {
        throw new Error('No open Seaweed business is assigned to this account.');
      }

      const portalRole =
        loginResponse.user?.role_name === 'admin-custom'
          ? 'admin-custom'
          : loginResponse.user?.role_name === 'accountant'
            ? 'accountant'
            : 'staff';
      const returnUrl = `${window.location.origin}/login`;
      const params = new URLSearchParams({
        businessId: String(business.id),
        portalRole,
        returnUrl,
        portalToken: loginResponse.token,
      });

      if (portalRole === 'admin-custom') {
        params.set('hideBackAction', '1');
      }

      setPortalSessionContext({
        businessId: String(business.id),
        portalRole,
        returnUrl,
        portalToken: loginResponse.token,
        hideBackAction: portalRole === 'admin-custom',
      });

      const rolePath =
        portalRole === 'staff'
          ? '/staff/dashboard'
          : portalRole === 'accountant'
            ? getAccountantEntryPath(getAccountantSeaweedAccessTabs())
            : '/admin/dashboard';
      const paramSeparator = rolePath.includes('?') ? '&' : '?';

      window.location.replace(`${rolePath}${paramSeparator}${params.toString()}`);
    } catch (requestError) {
      setError(requestError.message || 'Unable to login right now.');
      setIsSubmitting(false);
    }
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f8faf8] text-[#060a1d]">
      <div className="absolute left-0 right-0 top-0 h-[220px] bg-[linear-gradient(110deg,#0b8178_0%,#1397ad_34%,#8d8c68_66%,#d97722_100%)]" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-5xl flex-col items-center justify-center px-4 py-12">
        <form
          className="w-full max-w-[510px] overflow-hidden rounded-[28px] bg-white shadow-[0_28px_90px_rgba(42,35,28,0.16)]"
          onSubmit={handleSubmit}
        >
          <section className="bg-[#030719] px-8 pb-8 pt-6 text-white sm:px-9">
            <div className="flex items-start justify-between gap-4">
              <div className="flex h-[58px] w-[58px] items-center justify-center rounded-[18px] border border-white/10 bg-white/10 text-[#94e9ee] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]">
                <ShieldCheck size={24} strokeWidth={2.2} />
              </div>
              <span className="mt-3 rounded-full border border-white/15 px-4 py-1 text-xs font-bold uppercase tracking-[0.08em] text-white">
                Staff
              </span>
            </div>

            <h1 className="mt-8 text-[2rem] font-bold leading-tight tracking-[-0.02em]">
              Welcome back
            </h1>
            <p className="mt-3 max-w-sm text-base leading-7 text-white">
              Sign in to open your assigned workspace and continue daily operations.
            </p>
          </section>

          <section className="px-8 pb-8 pt-8 sm:px-9">
            <label className="text-sm font-semibold text-[#101632]" htmlFor="username">
              Staff ID
            </label>
            <div className="relative mt-2">
              <User
                aria-hidden="true"
                className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8797b7]"
              />
              <input
                autoComplete="username"
                className="h-[60px] w-full rounded-[18px] border border-[#d8c7b9] bg-[#fffbf6] pl-14 pr-4 text-base font-semibold text-[#101632] outline-none transition placeholder:text-[#93a0bd] focus:border-[#8f755f] focus:ring-4 focus:ring-[#8f755f]/10"
                id="username"
                name="username"
                onChange={handleChange}
                placeholder="Enter staff username"
                required
                value={formValues.username}
              />
            </div>

            <label className="mt-6 block text-sm font-semibold text-[#101632]" htmlFor="password">
              Password
            </label>
            <div className="relative mt-2">
              <Lock
                aria-hidden="true"
                className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8797b7]"
              />
              <input
                autoComplete="current-password"
                className="h-[60px] w-full rounded-[18px] border border-[#d8c7b9] bg-[#fffbf6] pl-14 pr-14 text-base font-semibold text-[#101632] outline-none transition placeholder:text-[#93a0bd] focus:border-[#8f755f] focus:ring-4 focus:ring-[#8f755f]/10"
                id="password"
                name="password"
                onChange={handleChange}
                placeholder="Enter password"
                required
                type={showPassword ? 'text' : 'password'}
                value={formValues.password}
              />
              <button
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-4 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-[#71809d] transition hover:bg-[#f0e7de] hover:text-[#4e3828]"
                onClick={() => setShowPassword((currentValue) => !currentValue)}
                type="button"
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>

            {error ? (
              <div className="mt-5 rounded-[16px] border border-[#efc8bd] bg-[#fff4f1] px-4 py-3 text-sm font-semibold text-[#9f3e2f]">
                {error}
              </div>
            ) : null}

            <button
              className="mt-7 inline-flex h-[62px] w-full items-center justify-center gap-3 rounded-[18px] bg-[#4a2415] px-5 text-base font-bold text-white shadow-[0_20px_36px_rgba(74,36,21,0.2)] transition hover:bg-[#5b2c19] disabled:cursor-not-allowed disabled:bg-[#a69082]"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? 'Opening workspace...' : 'Login'}
              {!isSubmitting ? <ArrowRight size={20} strokeWidth={2.2} /> : null}
            </button>
          </section>
        </form>

        <p className="mt-8 text-sm font-semibold text-[#60718a]">
          © 2026 RCM Ventures <span className="mx-2 text-[#9aa7b7]">|</span> All rights reserved
        </p>
      </div>
    </main>
  );
}

export default LoginPage;
